import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

/** Routes that load large amounts of data. Clearing their cache on leave frees memory. */
const HEAVY_ROUTES = [
  '/3danalysis_step1',
  '/3danalysis_step2',
  '/3danalysis_step3',
  '/3danalysis_step4',
  '/historical-declines',
  '/business-metrics',
  '/causality-report',
  '/360/owner-funnel',
  '/360/buyer-funnel',
  '/visual-generation',
  '/all-contacts',
  '/brand-team',
  '/brand-products',
  '/collaborations',
  '/cold-outreach',
  '/analytics',
  '/seo',
  '/articles',
  '/newsfeed',
];

/** Query keys to clear when leaving heavy pages. */
const HEAVY_QUERY_KEYS = [
  ['business-metrics'],
  ['business-metrics-chart-data'],
];

/**
 * Clears React Query cache for heavy pages when navigating away.
 * Call this hook from a component inside the router (e.g. App).
 */
export function useClearHeavyPageCache() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;

    const wasOnHeavyRoute = HEAVY_ROUTES.some((route) => prevPath.startsWith(route));
    const leftHeavyRoute = wasOnHeavyRoute && !HEAVY_ROUTES.some((route) => currentPath.startsWith(route));

    if (leftHeavyRoute) {
      // Remove specific heavy query keys
      HEAVY_QUERY_KEYS.forEach((queryKey) => {
        queryClient.removeQueries({ queryKey });
      });
      
      // Also remove ALL inactive queries to free memory on route change from heavy pages
      const cache = queryClient.getQueryCache();
      cache.getAll().forEach(query => {
        if (query.getObserversCount() === 0) {
          cache.remove(query);
        }
      });
    }

    prevPathRef.current = currentPath;
  }, [location.pathname, queryClient]);

  // Periodic cleanup: every 2 minutes, remove stale inactive queries
  useEffect(() => {
    const interval = setInterval(() => {
      const cache = queryClient.getQueryCache();
      const now = Date.now();
      let removed = 0;
      cache.getAll().forEach(query => {
        const isInactive = query.getObserversCount() === 0;
        const isOld = query.state.dataUpdatedAt && (now - query.state.dataUpdatedAt > 3 * 60 * 1000); // 3 min
        if (isInactive && isOld) {
          cache.remove(query);
          removed++;
        }
      });
      if (removed > 0) {
        console.log(`[CacheCleanup] Periodic cleanup removed ${removed} stale queries`);
      }
    }, 2 * 60 * 1000); // Every 2 minutes

    return () => clearInterval(interval);
  }, [queryClient]);
}
